import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getProposals } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PageHeader } from '@/components/ui/page-header';
import { StatusDot } from '@/components/ui/status-dot';
import { EmptyState } from '@/components/ui/empty-state';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Plus, Search, X } from 'lucide-react';

const formatCurrency = (a) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(a || 0);

/* Compact relative time for the read-receipt column — "3d", "2h", "just now". */
const relativeTime = (iso) => {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  const mins = Math.floor((Date.now() - then) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
};

/* Below 640px the table collapses: header hidden, each cell stacked with its micro label. */
const ROW = 'block cursor-pointer py-2 sm:table-row sm:py-0';
const CELL = 'flex h-auto items-baseline justify-between gap-4 px-0 py-1 before:text-micro before:uppercase before:text-ink-subtle before:content-[attr(data-label)] sm:table-cell sm:h-11 sm:px-3 sm:py-0 sm:before:content-none';

export default function Proposals() {
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const navigate = useNavigate();

  const fetch = async (s) => {
    setLoading(true);
    try { const res = await getProposals(s); setProposals(res.data); } catch { }
    setLoading(false);
  };

  useEffect(() => { fetch(filter); }, [filter]);

  const filtered = proposals.filter(p => {
    if (search) {
      const q = search.toLowerCase();
      const matchesName = (p.client_name || '').toLowerCase().includes(q);
      const matchesCompany = (p.client_company || '').toLowerCase().includes(q);
      const matchesTitle = (p.title || '').toLowerCase().includes(q);
      if (!matchesName && !matchesCompany && !matchesTitle) return false;
    }
    if (dateFrom && new Date(p.created_at) < new Date(dateFrom)) return false;
    if (dateTo) {
      const end = new Date(dateTo);
      end.setHours(23, 59, 59, 999);
      if (new Date(p.created_at) > end) return false;
    }
    return true;
  });

  const hasFilters = search || dateFrom || dateTo;
  const clearFilters = () => { setSearch(''); setDateFrom(''); setDateTo(''); };

  const open = (p) => navigate(`/proposals/${p.id}/edit`);

  return (
    <div data-testid="proposals-page">
      <PageHeader
        title="Proposals"
        description={`${filtered.length} proposal${filtered.length !== 1 ? 's' : ''}${hasFilters ? ` of ${proposals.length}` : ''}`}
        actions={
          <Button onClick={() => navigate('/proposals/new')} data-testid="new-proposal-btn">
            <Plus strokeWidth={1.5} /> New proposal
          </Button>
        }
      />

      <div className="space-y-4">
        <Tabs value={filter} onValueChange={setFilter}>
          <TabsList>
            <TabsTrigger value="all" data-testid="filter-all">All</TabsTrigger>
            <TabsTrigger value="draft" data-testid="filter-draft">Draft</TabsTrigger>
            <TabsTrigger value="sent" data-testid="filter-sent">Sent</TabsTrigger>
            <TabsTrigger value="accepted" data-testid="filter-accepted">Accepted</TabsTrigger>
            <TabsTrigger value="rejected" data-testid="filter-rejected">Rejected</TabsTrigger>
          </TabsList>
        </Tabs>

        <div className="flex flex-col gap-3 sm:flex-row">
          <div className="relative flex-1">
            <Search strokeWidth={1.5} className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-subtle" />
            <Input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search by client name, company, or title"
              className="pl-9"
              data-testid="search-proposals"
            />
          </div>
          <div className="flex items-center gap-2">
            <Input
              type="date"
              value={dateFrom}
              onChange={e => setDateFrom(e.target.value)}
              className="w-[168px] tabular-nums"
              data-testid="date-from"
            />
            <span className="text-small text-ink-subtle">to</span>
            <Input
              type="date"
              value={dateTo}
              onChange={e => setDateTo(e.target.value)}
              className="w-[168px] tabular-nums"
              data-testid="date-to"
            />
            {hasFilters && (
              <Button variant="ghost" size="icon" onClick={clearFilters} aria-label="Clear filters" data-testid="clear-filters">
                <X strokeWidth={1.5} />
              </Button>
            )}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="mt-6 space-y-px border-t border-line pt-px">
          {[1, 2, 3].map(i => <div key={i} className="h-11 animate-pulse bg-wash" />)}
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState
          className="mt-6"
          title={hasFilters ? 'No proposals match your filters' : 'No proposals yet'}
          description={hasFilters ? 'Try a different search term or date range.' : 'Write your first proposal and send it for signature.'}
          action={hasFilters ? (
            <Button variant="outline" onClick={clearFilters}>Clear filters</Button>
          ) : (
            <Button onClick={() => navigate('/proposals/new')}>New proposal</Button>
          )}
        />
      ) : (
        <div className="mt-6">
          <Table>
            <TableHeader className="hidden sm:table-header-group">
              <TableRow>
                <TableHead>Proposal</TableHead>
                <TableHead>Client</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Opened</TableHead>
                <TableHead className="text-right">Amount</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map(p => (
                <TableRow
                  key={p.id}
                  className={ROW}
                  role="link"
                  tabIndex={0}
                  onClick={() => open(p)}
                  onKeyDown={e => { if (e.key === 'Enter') open(p); }}
                  data-testid={`proposal-card-${p.id}`}
                >
                  <TableCell data-label="Proposal" className={`${CELL} text-ink`}>
                    <span className="min-w-0">
                      <span className="block truncate">{p.title}</span>
                      {p.status === 'rejected' && p.rejection_reason && (
                        <span className="mt-0.5 block truncate text-small text-status-overdue">Reason: {p.rejection_reason}</span>
                      )}
                    </span>
                  </TableCell>
                  <TableCell data-label="Client" className={`${CELL} text-ink-muted`}>
                    {p.client_name}{p.client_company ? ` (${p.client_company})` : ''}
                  </TableCell>
                  <TableCell data-label="Created" className={`${CELL} text-ink-muted`}>
                    {new Date(p.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell data-label="Status" className={CELL}><StatusDot status={p.status} /></TableCell>
                  <TableCell data-label="Opened" className={CELL}>
                    {p.status === 'draft' ? (
                      <span className="text-ink-subtle">—</span>
                    ) : p.view_count > 0 ? (
                      <span className="text-ink-muted">
                        {relativeTime(p.last_viewed_at)}
                        {p.view_count > 1 && (
                          <span className="text-ink-subtle tabular-nums"> · {p.view_count}×</span>
                        )}
                      </span>
                    ) : (
                      <span className="text-ink-subtle">Not opened</span>
                    )}
                  </TableCell>
                  <TableCell data-label="Amount" className={`${CELL} text-ink sm:text-right`}>{formatCurrency(p.total_amount)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
